import { NavigationStart } from "@angular/router";
import { filter } from "rxjs/operators";

export class ChunkErrorHandler {
  private static router: any;
  private static routeToRestore: string;
  private static readonly RESTORE_ROUTE_KEY = 'chunk-error-restore-route';
  private static readonly RETRY_KEY = 'chunk-error-retry-count';
  private static readonly MAX_RETRIES = 1;

  static init(router: any): void {
    this.router = router;
    this.router.events.pipe(
      filter((e) => e instanceof NavigationStart)
    )
      .subscribe(e => {
        this.routeToRestore = e.url;
      });
  }

  static handleLazyLoad<T>(importFn: () => Promise<T>, moduleName?: string): Promise<T> {
    return importFn().catch((error) => {
      if (!this.isChunkError(error)) throw error;

      this.log(error, moduleName);
      const retries = this.getRetries();

      if (retries < this.MAX_RETRIES) {
        this.storeRoute();
        window.location.reload();
        return new Promise<T>(() => { });
      }

      this.clearRetries();
      console.error('Max chunk load retries exceeded.');
      throw error;
    });
  }

  static restoreRoute(): void {
    const path = sessionStorage.getItem(this.RESTORE_ROUTE_KEY);
    if (!path || !this.router) return;

    const retries = this.getRetries();
    if (retries === 1) {
      this.clearRetries();
    } else {
      this.setRetries(retries + 1);
    }

    sessionStorage.removeItem(this.RESTORE_ROUTE_KEY);
    this.router.navigateByUrl(path);
  }

  private static isChunkError(error: any): boolean {
    return error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk');
  }

  private static log(error: any, moduleName?: string): void {
    console.error('ChunkLoadError:', {
      module: moduleName || 'unknown',
      message: error?.message || 'Unknown error'
    });
  }

  private static storeRoute(): void {
    if (this.routeToRestore) {
      sessionStorage.setItem(this.RESTORE_ROUTE_KEY, this.routeToRestore);
    }
  }

  private static getRetries(): number {
    return parseInt(sessionStorage.getItem(this.RETRY_KEY) || '0', 10);
  }

  private static setRetries(count: number): void {
    sessionStorage.setItem(this.RETRY_KEY, count.toString());
  }

  private static clearRetries(): void {
    sessionStorage.removeItem(this.RETRY_KEY);
  }
}

export const lazyLoad = <T>(importFn: () => Promise<T>, moduleName?: string): Promise<T> => {
  return ChunkErrorHandler.handleLazyLoad(importFn, moduleName);
};
